import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';

import { InterviewMessage } from '@empathy/ai-chat';
import { FormatWeekdayTime } from '@empathy/global/utils';

import { EnvironmentConfig } from '../config/environment.config';
import { AIInterviewStatistics } from '../models/ai-interview/ai-interview-statistics.model';
import { AiQuestionInsight } from '../models/ai-interview/ai-question-insight.interface';
import { AiQuestionItem } from '../models/ai-interview/ai-question-item.interface';
import { RunAiAnalysisResponse } from '../models/ai-interview/run-ai-analysis-response';
import { AiQASearch, AiQASearchHistory } from '../models/ai-qa-history.model';
import { AiTranscript, PendingAiTranscriptResult } from '../models/ai-transcript.model';
import { InsightAnswerCssClass, InsightAnswerLabelPrefix } from '../models/insight-answer-class.constant';

@Injectable({
  providedIn: 'root'
})
export class AiInterviewService {
  constructor(
    @Inject('environment') private environment: EnvironmentConfig,
    private http: HttpClient
  ) {}

  getAiSummary(projectId: string, methodId: string): Observable<AIInterviewStatistics> {
    return this.http.get<AIInterviewStatistics>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/overview`).pipe(
      map((transcript) => {
        const formattedTranscript = transcript;
        formattedTranscript.themes?.forEach((theme) => {
          theme.body = this.formatReference(theme.body);
        });

        formattedTranscript.quotes?.forEach((finding) => {
          finding.quote = this.formatReference(finding.quote);
        });

        formattedTranscript.opportunities?.forEach((opportunity) => {
          opportunity.body = this.formatReference(opportunity.body);
        });

        return formattedTranscript;
      }),
      catchError(() => of(null))
    );
  }

  getInterviewQAHistory(projectId: string, methodId: string): Observable<AiQASearchHistory> {
    return this.http
      .get<AiQASearchHistory>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/questions/history`)
      .pipe(
        map((result) => {
          result.searches = this.formatSearchesReferences(result.searches);
          return result;
        }),
        catchError(() => of(null))
      );
  }

  getAiInterviewPendingTranscripts(projectId: string, methodId: string): Observable<PendingAiTranscriptResult> {
    return this.http
      .get<PendingAiTranscriptResult>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/transcripts/pending`)
      .pipe(catchError(() => of(null)));
  }

  getAiQuestions(projectId: string, methodId: string): Observable<AiQuestionItem[]> {
    return this.http
      .get<AiQuestionItem[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/questions`)
      .pipe(catchError(() => of(null)));
  }

  getAiQuestionInsight(projectId: string, methodId: string, questionId: string): Observable<AiQuestionInsight> {
    return this.http
      .get<AiQuestionInsight>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/question/${questionId}/insight`)
      .pipe(
        tap((insight) => {
          const keysToProcess = ['themes', 'opportunities', 'patternsAndTrends', 'divergentViewsAndOutliers'];
          const referencesDict: { [x: string]: string } = {};

          if (insight.summary) {
            insight.summary = this.formatWithReferences(insight.summary, referencesDict);
          }

          keysToProcess.forEach((key) => {
            if (insight[key] && Array.isArray(insight[key])) {
              insight[key].forEach((theme) => {
                theme.body = this.formatWithReferences(theme.body, referencesDict);
              });
            }
          });

          insight.answers = insight.answers.filter((answer) => referencesDict[answer.id]);

          insight.answers?.forEach((answer) => {
            if (referencesDict[answer.id]) {
              answer.participant = `${InsightAnswerLabelPrefix} ${referencesDict[answer.id]}`;
            }
          });
        }),
        catchError(() => of(null))
      );
  }

  getAiTranscripts(projectId: string, methodId: string): Observable<AiTranscript[]> {
    return this.http
      .get<AiTranscript[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/transcripts`)
      .pipe(catchError(() => of(null)));
  }

  getAiTranscriptsById(projectId: string, methodId: string, conversationId: string): Observable<AiTranscript> {
    return this.http
      .get<AiTranscript[]>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/transcripts/${conversationId}`)
      .pipe(
        catchError(() => of(null)),
        map((transcripts) => (transcripts.length > 0 ? transcripts[0] : null)),
        tap((transcript) => {
          if (transcript) {
            transcript.messages.map((message: InterviewMessage) => {
              message.created_at = FormatWeekdayTime(message.created_at);
              return message;
            });
          }
        })
      );
  }

  deleteAiTranscriptById(projectId: string, methodId: string, conversationId: string): Observable<boolean> {
    return this.http.delete(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/transcripts/${conversationId}`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  sendAiInterviewQAQuestion(projectId: string, methodId: string, text: string): Observable<AiQASearch> {
    return this.http.post<AiQASearch>(`${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/questions`, { text }).pipe(
      map((result) => {
        result.answer = this.formatReference(result.answer);

        return result;
      }),
      catchError(() => of(null))
    );
  }

  runAIAnalysis(projectId: string, methodId: string): Observable<RunAiAnalysisResponse> {
    const url = `${this.environment.endPoint}project/${projectId}/research/${methodId}/ai-interview/analysis/run`;
    return this.http.post<RunAiAnalysisResponse>(url, {}).pipe(catchError(() => of()));
  }

  private formatSearchesReferences(searches: AiQASearch[]): AiQASearch[] {
    if (!searches || searches.length === 0) {
      return [];
    }

    return searches.map((search) => {
      search.answer = this.formatReference(search.answer);
      return search;
    });
  }

  private formatReference(answer: string, pattern: RegExp = /Ref: ((\d+)(,\s*\d+)*)/g): string {
    return answer.replace(pattern, (_match, capture) => {
      const numbers = capture.split(', ').map((n) => `${n}`);
      return `Ref <sup>${numbers.join(', ')}</sup>`;
    });
  }

  private extractAndFormatData(inputString: string): { conversationId: string; respondent_index: number }[] | null {
    const conversationIdRegex = /conversationId:\s*([a-f0-9]+)/g;
    const respondentIndexRegex = /respondent_index:\s*(\d+)/g;
    const matches: { conversationId: string; respondent_index: number }[] = [];
    let conversationIdMatch;
    let respondentIndexMatch;

    while ((conversationIdMatch = conversationIdRegex.exec(inputString)) !== null && (respondentIndexMatch = respondentIndexRegex.exec(inputString)) !== null) {
      const conversationId = conversationIdMatch[1];
      const respondentIndex = parseInt(respondentIndexMatch[1], 10);
      matches.push({ conversationId, respondent_index: respondentIndex });
    }

    return matches;
  }

  private replaceSequencesWithRespondentIndex(inputText: string): string {
    return inputText
      .replace(
        /{questionId:\s*[\w\-]+,\s*conversationId:\s*[\w\-]+,\s*respondent_index:\s*(\d+)}/g,
        (_, respondentIndex) => `<span id="ref-${respondentIndex}" class="${InsightAnswerCssClass} link"><sup>[${respondentIndex}]<sup></span>`
      )
      .replace(/respondent_index:\s*(\d+)/g, (_, respondentIndex) => `${InsightAnswerLabelPrefix} ${respondentIndex}`)
      .replace(/Respondent\s*(\d+)/g, (_, respondentIndex) => `${InsightAnswerLabelPrefix} ${respondentIndex}`);
  }

  private formatWithReferences(theme: string, referencesDict: { [x: string]: string }): string {
    const refs = this.extractAndFormatData(theme);
    refs.forEach((ref) => {
      referencesDict[ref.conversationId] = `${ref.respondent_index}`;
    });

    return this.replaceSequencesWithRespondentIndex(theme);
  }
}
